/* eslint-disable react-hooks/set-state-in-effect, react-hooks/set-state-in-render --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
import { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from "react";

/**
 * Cloudflare Turnstile widget.
 *
 * Loads the Turnstile script once (idempotent), renders a "managed"
 * widget which is invisible for trusted browsers and shows a small
 * challenge for suspicious traffic, and surfaces the token via
 * `onToken(token)`.
 *
 * Imperative API exposed via ref:
 *   ref.current.reset()  → resets the widget so a new token is issued
 *                          (call this after a failed submit so the
 *                          user can retry without a page reload).
 *
 * Hostname-aware site key:
 *   On production hostnames (`app.coparentconsole.com`,
 *   `coparentconsole.com`) the real site key from
 *   `REACT_APP_TURNSTILE_SITE_KEY` is used.
 *   On every other host (preview, emergent, localhost, custom) we
 *   swap in Cloudflare's documented "always-passes" test site key
 *   `1x00000000000000000000AA`. The backend pairs this with the
 *   matching test secret based on its own `Host` header, so the
 *   gate stays wired but never blocks non-production traffic.
 *
 * If `REACT_APP_TURNSTILE_SITE_KEY` is unset entirely (e.g. an
 * environment without Turnstile configured), the widget gracefully
 * renders nothing and immediately calls onToken("") so the form is
 * still submittable; the backend treats a missing secret as a
 * soft-skip too. NEVER deploy production without the key.
 */
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Cloudflare's documented "always-passes" test site key. Reference:
//   https://developers.cloudflare.com/turnstile/troubleshooting/testing/
const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";

const PRODUCTION_HOSTS = new Set([
  "app.coparentconsole.com",
  "coparentconsole.com",
]);

function resolveSiteKey() {
  const real = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  // SSR / non-browser → return real key (best-guess; never reached
  // in this CRA setup but defensive).
  if (typeof window === "undefined") return real || "";
  const host = (window.location && window.location.hostname) || "";
  if (PRODUCTION_HOSTS.has(host.toLowerCase())) return real || "";
  // Preview / emergent / localhost / unknown: use the always-passes
  // test key so the widget renders + issues a token even though the
  // hostname isn't whitelisted in the production Turnstile dashboard.
  return TURNSTILE_TEST_SITE_KEY;
}

let _scriptLoading = null;
function loadScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (_scriptLoading) return _scriptLoading;
  _scriptLoading = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile-script-failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile-script-failed"));
    document.head.appendChild(s);
  });
  return _scriptLoading;
}

const TurnstileWidget = forwardRef(function TurnstileWidget(
  { onToken, action = "submit", testId = "turnstile-widget" },
  ref,
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  // v77.99.11 — Surface CF errors instead of failing silently. The
  // widget's `error-callback` fires for hostname-not-whitelisted,
  // network-blocked, expired-key, etc. Without this, users on a
  // misconfigured production hostname see literally nothing in the
  // widget area and can't tell why their submit button is disabled.
  const [errorCode, setErrorCode] = useState("");
  const [scriptBlocked, setScriptBlocked] = useState(false);
  const siteKey = resolveSiteKey();

  const render = useCallback(() => {
    if (!siteKey || !window.turnstile || !containerRef.current) return;
    if (widgetIdRef.current) return;
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      action,
      // v77.99.11.1 — Switched from "interaction-only" to default
      // ("always") visibility. interaction-only renders nothing
      // visible by default and Cloudflare silently declines to
      // issue a token on some mobile carrier networks instead of
      // showing a challenge UI — leaving users with an empty widget
      // area and a permanently-disabled submit button. The default
      // "always" mode shows a small loading spinner and only
      // surfaces a checkbox when a real challenge is needed, so the
      // user always gets feedback that verification is happening.
      retry: "auto",
      "refresh-expired": "auto",
      callback: (token) => {
        setErrorCode("");
        if (onToken) onToken(token);
      },
      "error-callback": (code) => {
        // CF passes the error code (e.g. "110200" hostname-mismatch,
        // "300010" challenge expired). We stash it so the user gets a
        // specific message instead of a silent disabled button.
        setErrorCode(typeof code === "string" ? code : "unknown");
        if (onToken) onToken("");
      },
      "expired-callback": () => {
        setErrorCode("");
        if (onToken) onToken("");
      },
    });
  }, [siteKey, action, onToken]);

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) {
      onToken && onToken("");
      setLoaded(true);
      return;
    }
    loadScript()
      .then(() => {
        if (cancelled) return;
        setLoaded(true);
        setTimeout(render, 0);
      })
      .catch(() => {
        if (cancelled) return;
        setScriptBlocked(true);
        onToken && onToken("");
        setLoaded(true);
      });
    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch {
        // ignore
      }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    reset: () => {
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      } catch {
        // ignore
      }
      setErrorCode("");
    },
  }), []);

  if (!siteKey) {
    return null;
  }

  // Cloudflare error code → friendly explanation. Codes documented at
  // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/
  const errorMessage = (() => {
    if (scriptBlocked) {
      return "Verification couldn't load — your network or an extension may be blocking challenges.cloudflare.com.";
    }
    if (!errorCode) return "";
    if (errorCode.startsWith("110") || errorCode === "300030") {
      // 110200 = invalid domain / hostname not whitelisted
      return "Verification can't run on this domain. Please contact support — the site administrator needs to add this hostname to the Cloudflare Turnstile site config.";
    }
    if (errorCode === "300010" || errorCode === "300020") {
      return "Verification expired — please tap to refresh.";
    }
    return `Verification error (${errorCode}). Please refresh the page and try again.`;
  })();

  return (
    <div className="my-2" data-testid={testId}>
      <div ref={containerRef} />
      {!loaded && !errorCode && !scriptBlocked && (
        <div className="text-[11px] text-[#6F828A]" data-testid={`${testId}-loading`}>
          Loading verification…
        </div>
      )}
      {errorMessage && (
        <div
          className="mt-2 text-xs text-[#8C2A36] border border-[#F3D4D9] bg-[#FDF5F6] rounded-sm p-2"
          data-testid={`${testId}-error`}
        >
          {errorMessage}
        </div>
      )}
    </div>
  );
});

export default TurnstileWidget;
